// client/src/lib/parseNameList.js — turns a pasted list into Add Names rows
// (portal Add Names quick entry, Travis 10/27). Partners often arrive with 15
// or so names, copied from a spreadsheet (tab-separated) or typed in an
// email ("Jane Doe, Acme Capital, jane@acme.com"). Every parsed row lands in
// the grid for the partner to check, so this only has to be a good guess.
//
// With a header row (Name, Firm, Title, Email, Phone, Notes in any order and
// common spellings) columns map by header. Without one, an email or phone
// number is recognised wherever it sits, and the other cells are read in
// order as name, firm, title, then notes.

const HEADERS = {
  contact_name:  ['name', 'full name', 'contact', 'contact name', 'person', 'investor', 'investor name'],
  firm_name:     ['firm', 'company', 'organization', 'organisation', 'firm name', 'company name', 'fund', 'institution'],
  title_role:    ['title', 'role', 'position', 'job title'],
  email:         ['email', 'e-mail', 'email address'],
  phone:         ['phone', 'telephone', 'mobile', 'cell', 'phone number'],
  partner_notes: ['notes', 'note', 'comments', 'comment', 'relationship', 'why'],
}
const FIRST = ['first name', 'first', 'given name']
const LAST = ['last name', 'last', 'surname', 'family name']

const isEmail = s => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s)
const isPhone = s => /^[+()\d][\d\s().+-]{6,}$/.test(s) && (s.match(/\d/g) || []).length >= 7

function splitLine(line, tabbed) {
  if (tabbed) return line.split('\t').map(s => s.trim())
  // An email-style "Jane Doe <jane@acme.com>" keeps its address as a cell.
  const angled = line.replace(/<([^>]+@[^>]+)>/g, ', $1')
  for (const sep of [/\s*\|\s*/, /\s*;\s*/, /\s*,\s*/, /\s+[–—-]\s+/]) {
    const parts = angled.split(sep).map(s => s.trim()).filter(Boolean)
    if (parts.length > 1) return parts
  }
  return [angled.trim()]
}

function headerMap(cells) {
  const map = {}
  cells.forEach((c, i) => {
    const h = c.toLowerCase().replace(/[^a-z ]/g, '').trim()
    if (FIRST.includes(h)) map[i] = 'first'
    else if (LAST.includes(h)) map[i] = 'last'
    else for (const [field, names] of Object.entries(HEADERS)) if (names.includes(h)) map[i] = field
  })
  // A header row names at least two known columns, one of them a name.
  const fields = Object.values(map)
  const named = fields.includes('contact_name') || fields.includes('first')
  return named && fields.length >= 2 ? map : null
}

export function parseNameList(text) {
  const lines = String(text ?? '').split(/\r?\n/).filter(l => l.trim())
  if (!lines.length) return []
  const tabbed = lines.some(l => l.includes('\t'))
  const rows = lines.map(l => splitLine(l, tabbed))
  const map = headerMap(rows[0])
  const body = map ? rows.slice(1) : rows

  return body.map(cells => {
    const out = { contact_name: '', firm_name: '', title_role: '', email: '', phone: '', partner_notes: '' }
    if (map) {
      let first = '', last = ''
      cells.forEach((c, i) => {
        const f = map[i]
        if (!f || !c) return
        if (f === 'first') first = c
        else if (f === 'last') last = c
        else out[f] = out[f] ? `${out[f]} ${c}` : c
      })
      if (!out.contact_name && (first || last)) out.contact_name = [first, last].filter(Boolean).join(' ')
      return out
    }
    const rest = []
    for (const c of cells) {
      if (!c) continue
      if (!out.email && isEmail(c)) out.email = c
      else if (!out.phone && isPhone(c)) out.phone = c
      else rest.push(c)
    }
    ;[out.contact_name = '', out.firm_name = '', out.title_role = ''] = rest
    out.partner_notes = rest.slice(3).join(', ')
    return out
  }).filter(r => r.contact_name || r.firm_name || r.email)
}
